import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatCrvOption } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { useCrvOptions, useUpdateCrvOptions } from '@/hooks/use-crv-options';
import { toast } from '@/stores/toast-store';

/**
 * Parse a CRV input into cents. The field is DOLLARS by default — "8", "$8",
 * and "$8.00" all mean $8.00 (800¢). A trailing "¢" or "c" means cents, so
 * small cent values can still be entered directly ("25¢" / "25c" → 25¢).
 * Returns null when the text isn't a valid non-negative amount.
 */
function parseCrvInput(text: string): number | null {
  let s = text.trim();
  if (!s) return null;
  s = s.replace(/^\$/, '').trim();
  const isCents = /(¢|c)$/i.test(s);
  if (isCents) s = s.replace(/(¢|c)$/i, '').trim();
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  return isCents ? Math.round(n) : Math.round(n * 100);
}

/** Section label styling, matching the other product modals. */
const labelCls = 'block text-xs font-medium text-muted-foreground';

/**
 * Admin editor for the tenant's CRV fee options. Configured once per client —
 * the product + department CRV pickers read this list, so no code change is
 * needed per client. Values are cents (0 = None is always kept).
 */
export function CrvOptionsEditor({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { options } = useCrvOptions();
  const update = useUpdateCrvOptions();
  const [draft, setDraft] = useState<number[]>(options);
  const [input, setInput] = useState('');

  // Re-seed the draft each time the modal opens with the latest saved options.
  useEffect(() => {
    if (open) setDraft(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const parsed = parseCrvInput(input);

  function addValue() {
    if (parsed == null) {
      toast.error('Enter a valid amount');
      return;
    }
    if (draft.includes(parsed)) {
      toast.info('Already in the list');
    } else {
      setDraft((d) => [...d, parsed].sort((a, b) => a - b));
    }
    setInput('');
  }

  function removeValue(cents: number) {
    if (cents === 0) return; // "None" is always available
    setDraft((d) => d.filter((c) => c !== cents));
  }

  async function save() {
    try {
      await update.mutateAsync(draft);
      toast.success('CRV options saved');
      onClose();
    } catch {
      toast.error('Failed to save CRV options');
    }
  }

  // Everything except the always-present "None" (0) entry.
  const customCount = draft.filter((c) => c !== 0).length;
  const showError = input.trim() !== '' && parsed == null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Custom CRV fee options"
      subtitle="The CRV amounts offered when adding or editing a product. Configured once for this business."
      widthClass="max-w-[560px]"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="primary" size="sm" onClick={save} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div>
          <div className="mb-1.5 flex items-baseline justify-between gap-2">
            <span className={labelCls}>Current options</span>
            <span className="text-xs text-muted-foreground">
              {customCount} {customCount === 1 ? 'amount' : 'amounts'}
            </span>
          </div>
          {/* Chips sit on a panel so the list reads as one editable set rather
              than loose pills floating against the dialog background. */}
          <div className="flex flex-wrap gap-2 rounded-lg border border-border bg-muted/30 p-3">
            {draft.map((cents) => {
              // "None" is always available and can't be removed, so it reads as
              // a muted, static chip instead of an identical removable one.
              const locked = cents === 0;
              return (
                <span
                  key={cents}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border py-1 text-xs font-medium',
                    locked
                      ? 'border-dashed border-border bg-transparent px-3 text-muted-foreground'
                      : 'border-border bg-white pl-3 pr-1.5 text-foreground shadow-sm',
                  )}
                >
                  {formatCrvOption(cents)}
                  {!locked && (
                    <button
                      type="button"
                      onClick={() => removeValue(cents)}
                      className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
                      aria-label={`Remove ${formatCrvOption(cents)}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              );
            })}
            {customCount === 0 && (
              <span className="py-1 text-xs text-muted-foreground">No amounts yet — add one below.</span>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="crv-add-amount" className={labelCls}>
            Add an amount
          </label>
          <div className="flex items-center gap-2">
            <Input
              id="crv-add-amount"
              className="flex-1"
              value={input}
              error={showError}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addValue();
                }
              }}
              placeholder="e.g. 8, $8, 0.05, 25¢"
            />
            <Button type="button" variant="secondary" size="md" onClick={addValue} disabled={parsed == null}>
              Add
            </Button>
          </div>
          {/* One fixed-height line under the field: the format hint by default,
              swapped for the parse result while typing so the row never jumps. */}
          <p className="mt-1.5 min-h-[1.125rem] text-xs">
            {showError ? (
              <span className="text-red-600">Enter a valid amount.</span>
            ) : parsed != null ? (
              <span className="text-muted-foreground">
                Adds <span className="font-medium text-foreground">{formatCrvOption(parsed)}</span> to the list.
              </span>
            ) : (
              <span className="text-muted-foreground">
                Dollars by default — <span className="font-medium text-foreground">8</span> or{' '}
                <span className="font-medium text-foreground">$8</span> is $8.00. Add ¢ for cents, e.g.{' '}
                <span className="font-medium text-foreground">25¢</span>.
              </span>
            )}
          </p>
        </div>
      </div>
    </Modal>
  );
}
